import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Loader2, Plug, RotateCcw, Server, Wrench } from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { ScrollArea } from '@easyflow/ui-frontend/components/ui/scroll-area';
import { usePaConnectors } from './useConnectors';
import type { PaConnector } from '@easyflow/shared';

// Task #3818 — Sezione "Connettori" della sidebar PA: rende VISIBILE il
// perimetro effettivo (domini interni + server MCP del tenant) con tool
// espandibili e stato di salute. Ciò che è fuori perimetro NON arriva mai
// dal server, quindi non appare. Con una conversazione aperta, i toggle
// restringono i connettori attivi in QUELLA chat (mai oltre il perimetro).

interface Props {
  assistantId: string;
  /** Conversazione corrente: abilita i toggle per-chat. */
  currentConversationId: string | null;
}

const HEALTH_DOT: Record<PaConnector['health'], { cls: string; label: string }> = {
  healthy: { cls: 'bg-success', label: 'Operativo' },
  unhealthy: { cls: 'bg-danger', label: 'Non raggiungibile' },
  unknown: { cls: 'bg-text-faint', label: 'Stato sconosciuto' },
};

export const ConnectorsPanel: React.FC<Props> = ({ assistantId, currentConversationId }) => {
  const { connectors, mode, loading, saving, error, isActive, restrictedCount, toggle, reset } =
    usePaConnectors(assistantId, currentConversationId);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-2 py-2">
        <span className="text-[11px] font-semibold uppercase text-muted-foreground inline-flex items-center gap-1">
          <Plug className="h-3 w-3" /> Connettori
        </span>
        {restrictedCount > 0 && currentConversationId && (
            <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[10px]" onClick={() => void reset()} disabled={saving || loading} title="Riattiva tutti i connettori in questa chat">
            <RotateCcw className="h-2.5 w-2.5 mr-0.5" /> Ripristina
          </Button>
        )}
      </div>

      {mode === 'intersection' && (
        <div className="mx-2 mb-1.5 px-2 py-1 rounded bg-warning/10 border border-warning/30 text-[10px] text-warning">
          Perimetro: poteri dell'agente ∩ i tuoi permessi (modalità intersezione).
        </div>
      )}

      {error && (
        <div className="mx-2 mb-1.5 px-2 py-1 rounded bg-danger/10 border border-danger/30 text-[10px] text-danger">{error}</div>
      )}

      <ScrollArea className="flex-1 min-h-0">
        <div className="px-2 pb-2 space-y-1">
          {loading && connectors.length === 0 && (
            <div className="flex items-center justify-center py-6 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          )}
          {!loading && connectors.length === 0 && !error && (
            <div className="text-[11px] text-muted-foreground px-1 py-4 text-center">
              Nessun connettore nel tuo perimetro: l'amministratore non ha abilitato tool per questo assistente.
            </div>
          )}
          {connectors.map((c) => {
            const expanded = expandedId === c.id;
            const active = isActive(c.id);
            const dot = HEALTH_DOT[c.health];
            return (
              <div key={c.id} className={`rounded-lg border ${active ? 'border-border bg-card' : 'border-dashed border-border bg-muted/50 opacity-70'}`}>
                <div className="flex items-center gap-1.5 px-2 py-1.5">
                  <button
                    type="button"
                    onClick={() => setExpandedId(expanded ? null : c.id)}
                    className="shrink-0 text-muted-foreground hover:text-foreground"
                    title={expanded ? 'Chiudi dettaglio tool' : `Mostra i ${c.tools.length} tool`}
                  >
                    {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                  </button>
                  {c.kind === 'internal'
                    ? <Wrench className="h-3.5 w-3.5 shrink-0 text-ai" />
                    : <Server className="h-3.5 w-3.5 shrink-0 text-info" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-medium text-foreground truncate" title={c.label}>{c.label}</span>
                      <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${dot.cls}`} title={dot.label} />
                    </div>
                    {c.description && (
                      <div className="text-[10px] text-muted-foreground truncate" title={c.description}>{c.description}</div>
                    )}
                  </div>
                  <span className="text-[10px] text-muted-foreground shrink-0">{c.tools.length} tool</span>
                  {currentConversationId && (
                    <button
                      type="button"
                      role="switch"
                      aria-checked={active}
                      onClick={() => void toggle(c.id)}
                      disabled={saving || loading}
                      aria-label={`${active ? 'Disattiva' : 'Attiva'} ${c.label} in questa chat`}
                      className={`shrink-0 relative h-4 w-7 rounded-full transition ${active ? 'bg-ai' : 'bg-border-strong'} disabled:opacity-50`}
                      title={active ? 'Attivo in questa chat — clic per spegnere' : 'Spento in questa chat — clic per riattivare'}
                    >
                      <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition ${active ? 'left-3.5' : 'left-0.5'}`} />
                    </button>
                  )}
                </div>
                {expanded && (
                  <div className="border-t border-border px-2 py-1.5 space-y-1">
                    {c.tools.map((t) => (
                      <div key={t.name} className="text-[10px] leading-tight">
                        <span className="font-mono text-foreground">{t.name}</span>
                        {t.description && <span className="text-muted-foreground"> — {t.description}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </ScrollArea>

      {currentConversationId ? (
        <div className="px-2 py-1.5 border-t border-border text-[10px] text-muted-foreground">
          {restrictedCount > 0
            ? `${restrictedCount} connettor${restrictedCount === 1 ? 'e spento' : 'i spenti'} in questa chat.`
            : 'Tutti i connettori del perimetro sono attivi in questa chat.'}
        </div>
      ) : (
        <div className="px-2 py-1.5 border-t border-border text-[10px] text-muted-foreground">
          Apri una conversazione per restringere i connettori di quella chat.
        </div>
      )}
    </div>
  );
};
